import {
    Box,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Button,
    TableContainer,
    TableHead,
    Table,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Checkbox,
    Tooltip,
    TableSortLabel
} from '@mui/material';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as React from 'react';
import NewsEditorModal from './NewsEditorModal';
import NewsItem, { INewsItem, NewsStatusLabels } from './NewsItem';
import { NewsDisplayStatus } from './newsDisplayStatus';
import {
    filterAndSortNews,
    filtersFromSearch,
    filtersToSearch,
    NewsListFilters,
    NewsSortColumn,
    nextSort
} from './newsListFilters';
import { NewsApi, NewsItemPayload } from './NewsApi';
import { Translate } from '../../shared/LanguageService';
import { todayNewsDateText } from './newsDates';
import { getRoleOptions, transformToPayload, NewsItemStatus } from './NewsUtils';
import { UserRole } from '../../components/common/header/photo/head-bar/newsComponent/utils/userRoleHelper';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTelemetryContext } from '../../telemetry/telemetryContext';
import { getUserInfo } from '../../telemetry/utils';
import { useSelector } from 'react-redux';
import { getUser } from '../../reducers/userReducer';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';

type NewsEditorLabels = {
    pageTitle: string;
    searchPlaceholder: string;
    filterRecipientsPlaceholder: string;
    roleAdmin: string;
    roleBrokerAgent: string;
    rolePhotographer: string;
    rolePhotoEditor: string;
    rolePlanEditor: string;
    filterStatusLabel: string;
    allStatuses: string;
    createNewsButton: string;
    columnVisibleDate: string;
    columnTitle: string;
    columnStartDate: string;
    columnEndDate: string;
    columnStatus: string;
    statusLabels: NewsStatusLabels;
    startDateHint: string;
    visibleDateHint: string;
    endDateHint: string;
    columnRecipients: string;
    columnReadBy: string;
    columnVisible: string;
    columnEdit: string;
    newsItemEditButtonLabel: string;
};

type BrokerChain = {
    id: number;
    name: string;
};

type NewsEditorProps = RouteComponentProps & {
    newsItems: INewsItem[];
    brokerChains: BrokerChain[];
    brokerChainsLoading: boolean;
    toggleVisibility: (item: NewsItemPayload, visible: boolean) => Promise<void>;
    labels: NewsEditorLabels;
    createItem: (item: NewsItemPayload) => void;
    updateItem: (item: NewsItemPayload) => void;
};

type SortableColumnHeaderProps = {
    label: string;
    column: NewsSortColumn;
    filters: NewsListFilters;
    onSort: (column: NewsSortColumn) => void;
    hint?: string;
};

function SortableColumnHeader({ label, column, filters, onSort, hint }: SortableColumnHeaderProps) {
    const isSorted = filters.sort === column;
    return (
        <TableCell sx={{ fontWeight: 600 }} sortDirection={isSorted ? filters.direction : false}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <TableSortLabel
                    active={isSorted}
                    direction={isSorted ? filters.direction : 'asc'}
                    onClick={() => onSort(column)}
                >
                    {label}
                </TableSortLabel>
                {hint ? (
                    <Tooltip title={hint}>
                        <InfoOutlinedIcon
                            aria-label={hint}
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                    </Tooltip>
                ) : null}
            </Box>
        </TableCell>
    );
}

const statusOrder = [
    NewsDisplayStatus.Draft,
    NewsDisplayStatus.Scheduled,
    NewsDisplayStatus.Live,
    NewsDisplayStatus.Expired,
    NewsDisplayStatus.Archived
];

export function NewsEditor({
    location,
    history,
    newsItems,
    brokerChains,
    brokerChainsLoading,
    createItem,
    updateItem,
    toggleVisibility,
    labels
}: NewsEditorProps) {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [editingNews, setEditingNews] = React.useState<INewsItem | null>(null);
    const filters = filtersFromSearch(location.search);

    function updateFilters(nextFilters: NewsListFilters) {
        history.replace({ ...location, search: filtersToSearch(nextFilters) });
    }

    function handleSort(column: NewsSortColumn) {
        updateFilters(nextSort(filters, column));
    }

    const roles = getRoleOptions();
    const today = todayNewsDateText();

    const handleEditNews = (id: number) => {
        const newsToEdit = newsItems.find((n) => n.id === id);
        setEditingNews(newsToEdit || null);
        setModalOpen(true);
    };

    async function saveNews(item: INewsItem, brokerChainIds: number[]) {
        const payload = transformToPayload(item, brokerChainIds);

        if (editingNews) {
            return updateItem(payload);
        }

        return createItem(payload);
    }

    const handleToggleVisible = async (id: number) => {
        const itemToUpdate = newsItems.find((item) => item.id === id);
        if (!itemToUpdate) return;

        const newValue = !itemToUpdate.visible;

        const payload = transformToPayload({
            ...itemToUpdate,
            visible: newValue,
            status: newValue ? NewsItemStatus.Active : NewsItemStatus.Draft
        });

        await toggleVisibility(payload, newValue);
    };

    const filteredNews = filterAndSortNews(newsItems, filters, today);

    return (
        <Box>
            <Typography variant="h1" sx={{ mt: '20px', mb: '10px', fontSize: '33px' }}>
                {labels.pageTitle}
            </Typography>
            <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Box display="flex" gap={2} sx={{ flex: 1, maxWidth: 600 }}>
                    <TextField
                        fullWidth
                        id="outlined-basic"
                        label={labels.searchPlaceholder}
                        variant="outlined"
                        value={filters.search}
                        onChange={(e) => updateFilters({ ...filters, search: e.target.value })}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0
                            }
                        }}
                    />

                    <FormControl
                        fullWidth
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0
                            }
                        }}
                    >
                        <InputLabel shrink>{labels.filterRecipientsPlaceholder}</InputLabel>
                        <Select
                            notched
                            label={labels.filterRecipientsPlaceholder}
                            value={filters.recipient}
                            onChange={(e) => {
                                updateFilters({ ...filters, recipient: e.target.value as string });
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: { backgroundColor: 'rgb(255, 255, 255)' }
                                }
                            }}
                        >
                            <MenuItem value="">{labels.filterRecipientsPlaceholder}</MenuItem>
                            <MenuItem value={UserRole.Admin}>{labels.roleAdmin}</MenuItem>
                            <MenuItem value={UserRole.BrokerAgent}>
                                {labels.roleBrokerAgent}
                            </MenuItem>
                            <MenuItem value={UserRole.Photographer}>
                                {labels.rolePhotographer}
                            </MenuItem>
                            <MenuItem value={UserRole.PhotoEditor}>
                                {labels.rolePhotoEditor}
                            </MenuItem>
                            <MenuItem value={UserRole.PlanEditor}>{labels.rolePlanEditor}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <FormControl
                    sx={{
                        minWidth: 220,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 0
                        }
                    }}
                >
                    <InputLabel id="news-status-filter-label" shrink>
                        {labels.filterStatusLabel}
                    </InputLabel>
                    <Select
                        multiple
                        notched
                        displayEmpty
                        labelId="news-status-filter-label"
                        label={labels.filterStatusLabel}
                        value={filters.statuses}
                        onChange={(e) =>
                            updateFilters({
                                ...filters,
                                statuses: statusOrder.filter(
                                    (status) =>
                                        (e.target.value as NewsDisplayStatus[]).indexOf(status) !==
                                        -1
                                )
                            })
                        }
                        renderValue={(selected) =>
                            (selected as NewsDisplayStatus[]).length === 0
                                ? labels.allStatuses
                                : (selected as NewsDisplayStatus[])
                                      .map((status) => labels.statusLabels[status])
                                      .join(', ')
                        }
                        MenuProps={{
                            PaperProps: {
                                sx: { backgroundColor: 'rgb(255, 255, 255)' }
                            }
                        }}
                    >
                        {statusOrder.map((status) => (
                            <MenuItem key={status} value={status}>
                                <Checkbox
                                    size="small"
                                    checked={filters.statuses.indexOf(status) !== -1}
                                />
                                <ListItemText primary={labels.statusLabels[status]} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    size="medium"
                    color="primary"
                    sx={{ ml: 'auto', textTransform: 'none', borderRadius: 0 }}
                    onClick={() => {
                        setEditingNews(null);
                        setModalOpen(true);
                    }}
                >
                    {labels.createNewsButton}
                </Button>
            </Box>

            <TableContainer>
                <Table
                    sx={{
                        borderCollapse: 'collapse'
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '8px', p: 0 }}></TableCell>
                            <SortableColumnHeader
                                label={labels.columnTitle}
                                column={NewsSortColumn.Title}
                                filters={filters}
                                onSort={handleSort}
                            />
                            <SortableColumnHeader
                                label={labels.columnStatus}
                                column={NewsSortColumn.Status}
                                filters={filters}
                                onSort={handleSort}
                            />
                            <SortableColumnHeader
                                label={labels.columnStartDate}
                                column={NewsSortColumn.StartDate}
                                filters={filters}
                                onSort={handleSort}
                                hint={labels.startDateHint}
                            />
                            <SortableColumnHeader
                                label={labels.columnEndDate}
                                column={NewsSortColumn.EndDate}
                                filters={filters}
                                onSort={handleSort}
                                hint={labels.endDateHint}
                            />
                            <SortableColumnHeader
                                label={labels.columnVisibleDate}
                                column={NewsSortColumn.VisibleDate}
                                filters={filters}
                                onSort={handleSort}
                                hint={labels.visibleDateHint}
                            />
                            <TableCell sx={{ fontWeight: 600 }}>
                                {labels.columnRecipients}
                            </TableCell>
                            <SortableColumnHeader
                                label={labels.columnReadBy}
                                column={NewsSortColumn.ReadBy}
                                filters={filters}
                                onSort={handleSort}
                            />
                            <TableCell sx={{ fontWeight: 600 }}>{labels.columnVisible}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{labels.columnEdit}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredNews.map((item) => (
                            <NewsItem
                                key={item.id}
                                news={item}
                                onEdit={handleEditNews}
                                editButtonLabel={labels.newsItemEditButtonLabel}
                                onToggleVisible={handleToggleVisible}
                                statusLabels={labels.statusLabels}
                                today={today}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <NewsEditorModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                roles={roles}
                brokerChains={brokerChains}
                brokerChainsLoading={brokerChainsLoading}
                onPublish={saveNews}
                onSaveDraft={saveNews}
                editingNews={editingNews}
            />
        </Box>
    );
}

const NewsEditorWithRouter = withRouter(NewsEditor);

enum QueryKey {
    GetAllNewsItems = 'get-all-news-items',
    GetBrokerChains = 'get-broker-chains'
}

function TranslationLayer({
    newsItems,
    brokerChains,
    brokerChainsLoading,
    toggleVisibility,
    createItem,
    updateItem
}: {
    newsItems: INewsItem[];
    brokerChains: BrokerChain[];
    brokerChainsLoading: boolean;
    toggleVisibility: (item: NewsItemPayload, visible: boolean) => Promise<void>;
    createItem: (item: NewsItemPayload) => void;
    updateItem: (item: NewsItemPayload) => void;
}) {
    const labels: NewsEditorLabels = {
        pageTitle: Translate('news-editor/page-head'),
        searchPlaceholder: Translate('news-editor/search-placeholder'),
        filterRecipientsPlaceholder: Translate('news-editor/filter-recipients-placeholder'),
        roleAdmin: Translate(UserRole.Admin),
        roleBrokerAgent: Translate(UserRole.BrokerAgent),
        rolePhotographer: Translate(UserRole.Photographer),
        rolePhotoEditor: Translate(UserRole.PhotoEditor),
        rolePlanEditor: Translate(UserRole.PlanEditor),
        filterStatusLabel: Translate('news-editor/column-status'),
        allStatuses: Translate('news-editor/all-statuses'),
        createNewsButton: Translate('news-editor/create-news-button'),
        columnVisibleDate: Translate('news-editor/column-visible-date'),
        columnTitle: Translate('news-editor/column-title'),
        columnStartDate: Translate('news-editor/column-start-date'),
        columnEndDate: Translate('news-editor/column-end-date'),
        columnStatus: Translate('news-editor/column-status'),
        statusLabels: {
            [NewsDisplayStatus.Draft]: Translate('news-editor/status/draft'),
            [NewsDisplayStatus.Scheduled]: Translate('news-editor/status/scheduled'),
            [NewsDisplayStatus.Live]: Translate('news-editor/status/live'),
            [NewsDisplayStatus.Expired]: Translate('news-editor/status/expired'),
            [NewsDisplayStatus.Archived]: Translate('news-editor/status/archived')
        },
        startDateHint: Translate('news-editor/start-date-hint'),
        visibleDateHint: Translate('news-editor/visible-date-hint'),
        endDateHint: Translate('news-editor/end-date-hint'),
        columnRecipients: Translate('news-editor/column-recipients'),
        columnReadBy: Translate('news-editor/column-read-by'),
        columnVisible: Translate('news-editor/column-visible'),
        columnEdit: Translate('news-editor/column-edit'),
        newsItemEditButtonLabel: Translate('news-editor/edit-news-button')
    };

    return (
        <NewsEditorWithRouter
            updateItem={updateItem}
            createItem={createItem}
            newsItems={newsItems}
            brokerChains={brokerChains}
            brokerChainsLoading={brokerChainsLoading}
            labels={labels}
            toggleVisibility={toggleVisibility}
        />
    );
}

export default function DataLayer() {
    const { logger } = useTelemetryContext();
    const user = useSelector((state) => getUser(state));
    const queryClient = useQueryClient();
    const [showToggleError, setShowToggleError] = React.useState(false);

    const { data, isError, error, isLoading } = useQuery<INewsItem[]>(
        [QueryKey.GetAllNewsItems],
        () => NewsApi.getNewsItems()
    );

    const {
        data: brokerChainsData,
        isLoading: brokerChainsLoading,
        isError: brokerChainsIsError,
        error: brokerChainsError
    } = useQuery<BrokerChain[]>([QueryKey.GetBrokerChains], () => NewsApi.getBrokerChains());

    const brokerChains = brokerChainsData ? brokerChainsData : [];

    const toggleVisibilityMutation = useMutation(
        ({ updatedItem, visible }: { updatedItem: NewsItemPayload; visible: boolean }) =>
            NewsApi.toggleNewsVisibility(updatedItem, visible),
        {
            onMutate: async ({ updatedItem, visible }) => {
                await queryClient.cancelQueries([QueryKey.GetAllNewsItems]);

                const previousNewsItems = queryClient.getQueryData<INewsItem[]>([
                    QueryKey.GetAllNewsItems
                ]);

                if (previousNewsItems && Array.isArray(previousNewsItems)) {
                    const nextNewsItems = previousNewsItems.map((newsItem) => {
                        if (newsItem.id !== updatedItem.id) {
                            return newsItem;
                        }
                        return { ...newsItem, visible, status: updatedItem.status };
                    });

                    queryClient.setQueryData([QueryKey.GetAllNewsItems], nextNewsItems);
                }

                return { previousNewsItems };
            },
            onError: (err: unknown, _vars, context: any) => {
                if (context && context.previousNewsItems) {
                    queryClient.setQueryData([QueryKey.GetAllNewsItems], context.previousNewsItems);
                }
                setShowToggleError(true);
            },
            onSettled: () => {
                queryClient.invalidateQueries([QueryKey.GetAllNewsItems]);
            }
        }
    );

    async function handleToggleVisibility(item: NewsItemPayload, visible: boolean): Promise<void> {
        await toggleVisibilityMutation.mutateAsync({ updatedItem: item, visible });
    }

    const updateItemMutation = useMutation(
        ({ item }: { item: NewsItemPayload }) => NewsApi.updateNewsItem(item),
        {
            onSuccess: () => queryClient.invalidateQueries([QueryKey.GetAllNewsItems])
        }
    );

    function handleUpdate(item: NewsItemPayload) {
        return updateItemMutation.mutateAsync({ item });
    }

    const createItemMutation = useMutation(
        ({ item }: { item: NewsItemPayload }) => NewsApi.createNewsItem(item),
        {
            onSuccess: () => queryClient.invalidateQueries([QueryKey.GetAllNewsItems])
        }
    );

    function handleCreate(item: NewsItemPayload) {
        return createItemMutation.mutateAsync({ item });
    }

    if (isError) {
        const additionalInfo = {
            origin: 'NewsEditorComponent',
            query: QueryKey.GetAllNewsItems,
            userInfo: getUserInfo(user)
        };

        logger.logException(error, additionalInfo);

        return null;
    }

    if (isLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2">{Translate('news-editor/loading-message')}</Typography>
            </Box>
        );
    }

    if (brokerChainsIsError) {
        logger.logException(brokerChainsError, {
            origin: 'NewsEditorComponent',
            query: QueryKey,
            userInfo: getUserInfo(user)
        });
    }

    const newsItems: INewsItem[] = data ? data : [];

    return (
        <>
            <TranslationLayer
                newsItems={newsItems}
                brokerChains={brokerChains}
                brokerChainsLoading={brokerChainsLoading}
                updateItem={handleUpdate}
                createItem={handleCreate}
                toggleVisibility={handleToggleVisibility}
            />
            <FailureSnackbar
                open={showToggleError}
                onClose={function () {
                    setShowToggleError(false);
                }}
                text={Translate('news-editor/toggle-error')}
            />
        </>
    );
}
